import React from 'react';
import moment from 'moment-timezone';
import {Dropdown, Text, Icon, ListGroupItem, Button, Input, Label, Field} from '@albert-io/atomic';
import {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {ClassroomAssignmentSettings} from './RecipientSettings.types';
import {Map} from 'immutable';
import {handleDateChange, validDateRange, validTimeRange} from '../settingsModal.utils';

import './classroom-table.scss';

interface StartDateDropdownProps {
  classroomObj: ClassroomModelV1;
  selectedClassrooms: Map<string, ClassroomAssignmentSettings>;
  setSelectedClassrooms: Function;
}

const StartDateDropdown = ({
  classroomObj,
  selectedClassrooms,
  setSelectedClassrooms
}: StartDateDropdownProps) => {
  const classroomId = classroomObj.classroomInfo.getId();
  const hasDateError = !validDateRange(classroomObj.startDate, classroomObj.dueDate);
  const hasTimeError = !validTimeRange(
    classroomObj.startDate,
    classroomObj.dueDate,
    classroomObj.startDateTime,
    classroomObj.dueDateTime
  );

  return (
    <Dropdown
      trigger={
        <button type='button' className='classroom-table__dropdown-button'>
          <>
            {classroomObj.startDate && classroomObj.startDateTime ? (
              <div className='u-display_flex u-justify-content_space-between u-width_100pc u-align-items_center'>
                <div
                  style={{gap: '0.3em'}}
                  className='u-display_flex u-flex-direction_column u-text-align_left'
                >
                  <Text size='m'>
                    {moment(`${classroomObj.startDate} ${classroomObj.startDateTime} `).format(
                      'MMM DD, YYYY'
                    )}
                  </Text>
                  <Text size='m' emphasis='lowest'>
                    {`${moment(`${classroomObj.startDate} ${classroomObj.startDateTime} `).format(
                      'hh:mm a'
                    )} ${moment.tz(moment.tz.guess()).zoneAbbr()}`}
                  </Text>
                </div>
                <Icon icon='caret-down' color='midnight-lightest' className='u-mar-r_1' />
              </div>
            ) : (
              <div className='u-display_flex u-justify-content_space-between u-width_100pc u-align-items_center'>
                <Text size='m'>Immediately</Text>
                <Icon icon='caret-down' color='midnight-lightest' className='u-mar-r_1' />
              </div>
            )}
          </>
        </button>
      }
      className='classroom-table__dropdown'
    >
      <ListGroupItem onClick={(e) => e.stopPropagation()}>
        <div className='u-mar-b_2'>
          <Label>
            <Field
              as={Input}
              label='Day'
              type='date'
              message={hasDateError && 'Start date cannot be after due date.'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                handleDateChange(
                  classroomObj,
                  {startDate: e.target.value},
                  selectedClassrooms,
                  setSelectedClassrooms,
                  classroomId
                );
              }}
              error={hasDateError}
              value={classroomObj.startDate}
            />
          </Label>
        </div>
        <div>
          <Label htmlFor='time'>
            <Field
              as={Input}
              label='Time'
              type='time'
              message={hasTimeError && 'Start time cannot be after due date time.'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                handleDateChange(
                  classroomObj,
                  {startDateTime: e.target.value},
                  selectedClassrooms,
                  setSelectedClassrooms,
                  classroomId
                );
              }}
              error={hasTimeError}
              value={classroomObj.startDate ? classroomObj.startDateTime : ''}
            />
          </Label>
        </div>
      </ListGroupItem>
      <div className='classroom-table__dropdown-button-group u-pad_1 u-display_flex u-justify-content_space-between'>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Button
            size='s'
            variant='text'
            disabled={!classroomObj.startDate && !classroomObj.startDateTime}
            onClick={() => {
              handleDateChange(
                classroomObj,
                {startDate: '', startDateTime: '06:00:00'},
                selectedClassrooms,
                setSelectedClassrooms,
                classroomId
              );
            }}
          >
            Set to &quot;Immediately&quot;
          </Button>
        </div>
        <Button size='s' variant='text' disabled={hasDateError || hasTimeError}>
          Done
        </Button>
      </div>
    </Dropdown>
  );
};

export default StartDateDropdown;
